import React, { useState } from "react";
import { useSelector } from "react-redux";
import SubmitButton from "../submitButton/SubmitButton";
import { useNotifications } from "../../context/Notifications";
import { useTranslator } from "../../context/translator";
import CardOwnerForm from "components/cardOwnerForm/CardOwnerForm";
import { uploadFileToAzure } from "utils/azure/azure";
import {
  createSensitiveCustomerInformationService,
  updateSensitiveCustomerInformationService,
} from "services/sensitiveCustomerInformation/services";
import { DOCUMENT_TYPES } from "utils/enum/enums";
import DocumentForm from "components/documentForm/DocumentForm";
import DocumentUtilityBillForm from "components/documentUtilityBillForm/DocumentUtilityBillForm";
import DocumentIdentificationForm from "components/documentIdentificationForm/DocumentIdentificationForm";
import { useGetUserByIdQuery } from "store/api/apiSlice";

function ClientCardInformationModal({
  clientCardInformation,
  setClientCardInformation,
  getInformation,
  modalOpen,
  toggleModal,
  customer,
}) {
  const { user } = useSelector((state) => state.user);
  const { addNotification } = useNotifications();

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const [isLoading, setIsLoading] = useState(false);
  const { translate } = useTranslator();
  const [form, setForm] = useState({});

  const handleSaveFiles = async () => {
    const filesNotSave = clientCardInformation.files.filter(
      (file) => !file.url,
    );
    const filesSave =
      clientCardInformation.files.filter((file) => file.url) || [];

    const files = filesNotSave;

    if (!files.length) return filesSave;

    setIsLoading(true);

    try {
      const result = await Promise.all(
        files.map(async (file) => {
          const url = await uploadFileToAzure(file, userProfile?.user);

          return {
            url,
            type: file.type, // ej: "image/png"
            name: file.name, // ej: "documento.png"
          };
        }),
      );

      return result.concat(filesSave);
    } catch (error) {
      addNotification({
        id: Date.now(),
        message: "Error uploading files",
        kind: "danger",
      });

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsLoading(true);

    const uploadedFiles = await handleSaveFiles();

    setClientCardInformation((prev) => ({
      ...prev,
      files: uploadedFiles,
      cardNumber: undefined,
    }));

    if (clientCardInformation?._id) {
      const response = await updateSensitiveCustomerInformationService({
        sensitiveCustomerInformationId: clientCardInformation._id,
        updateFields: {
          ...clientCardInformation,
          files: uploadedFiles,
          cardNumber: undefined,
        },
      });

      if (response?.missingFields) {
        setForm(response?.missingFields);
      }

      if (response?.sensitiveCustomerInformation?.acknowledged) {
        await getInformation();
        addNotification({
          id: Date.now(),
          message: translate("document-updated"),
          kind: "success",
        });

        setIsLoading(false);
        toggleModal();
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-document-update"),
          kind: "danger",
        });
      }
    } else {
      const response = await createSensitiveCustomerInformationService({
        data: {
          ...clientCardInformation,
          files: uploadedFiles,
          cardNumber: undefined,
          customerId: customer?._id,
        },
      });

      if (response?.missingFields) {
        setForm(response?.missingFields);
      }

      if (response?.sensitiveCustomerInformation?.acknowledged) {
        await getInformation();
        addNotification({
          id: Date.now(),
          message: translate("document-created"),
          kind: "success",
        });

        setIsLoading(false);
        toggleModal();
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-document-creation"),
          kind: "danger",
        });
      }
    }

    setIsLoading(false);
  };

  if (userLoading) return null;

  return (
    <>
      {modalOpen && (
        <div
          id="modalID"
          tabIndex="-1"
          aria-hidden="true"
          className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto"
        >
          <div className="relative p-4 w-full max-w-2xl max-h-full">
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
              <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {translate("new-document")}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="modalID"
                  onClick={toggleModal}
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5">
                {!clientCardInformation?._id && (
                  <div className="mb-4 ">
                    <label className="label">
                      {translate("document-type")}
                    </label>
                    <select
                      className="mb-4 p-2 border border-gray-300 rounded"
                      value={clientCardInformation.documentType}
                      onChange={(e) =>
                        setClientCardInformation({
                          ...clientCardInformation,
                          documentType: e.target.value,
                        })
                      }
                    >
                      <option value={DOCUMENT_TYPES.CD}>
                        {translate("card")}
                      </option>
                      <option value={DOCUMENT_TYPES.DC}>
                        {translate("document")}
                      </option>
                      <option value={DOCUMENT_TYPES.ID}>
                        {translate("identification")}
                      </option>
                      <option value={DOCUMENT_TYPES.UB}>
                        {translate("utility-bill")}
                      </option>
                    </select>
                  </div>
                )}
                <form method="POST" onSubmit={handleSave}>
                  <div className="grid gap-6 mb-6 md:grid-cols-1">
                    {clientCardInformation.documentType ===
                      DOCUMENT_TYPES.CD && (
                      <CardOwnerForm
                        key={"cardOwnerForm"}
                        data={clientCardInformation}
                        onChange={setClientCardInformation}
                        form={form}
                        customerId={customer?._id}
                      />
                    )}
                    {clientCardInformation.documentType ===
                      DOCUMENT_TYPES.DC && (
                      <DocumentForm
                        key={"documentForm"}
                        data={clientCardInformation}
                        onChange={setClientCardInformation}
                        form={form}
                      />
                    )}
                    {clientCardInformation.documentType ===
                      DOCUMENT_TYPES.ID && (
                      <DocumentIdentificationForm
                        key={"documentIdentificationForm"}
                        data={clientCardInformation}
                        onChange={setClientCardInformation}
                        form={form}
                      />
                    )}
                    {clientCardInformation.documentType ===
                      DOCUMENT_TYPES.UB && (
                      <DocumentUtilityBillForm
                        key={"documentUtilityBillForm"}
                        data={clientCardInformation}
                        onChange={setClientCardInformation}
                        form={form}
                      />
                    )}

                    <br />
                    <SubmitButton
                      isLoading={isLoading}
                      textLoading={translate("saving")}
                      text={translate("save")}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientCardInformationModal;
